import { useState, useCallback, useContext } from "react";
import { PublicKey, TransactionInstruction } from "@solana/web3.js";
import {
    getAssociatedTokenAddress,
    createAssociatedTokenAccountIdempotentInstruction,
    TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { useAnchorWallet, useConnection, useWallet } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import { createDripRedeemPayload, createDripRedeemAssetPayload } from "../helpers/payloads";
import { DripVaultDescriptor } from "../constants/dripVaults";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { deriveDripPdas } from "../features/drip/dripPdas";
import { dripProgramFor, fetchDripOracleAccounts } from "../features/drip/dripFetch";
import { sendTx } from "./utils/utils";
import { getWalletErrorMessage } from "./utils/walletErrors";
import mixpanel from "mixpanel-browser";

// The Drip program has two instruction families (legacy kySOL vs. generic
// asset vaults) whose encoders are byte-identical in signature — only the
// discriminator differs. That means a call site can invoke the wrong
// encoder for a vault's kind, compile, and silently target the wrong
// on-chain instruction. Route every redeem through this function so the
// kind -> discriminator mapping lives in exactly one place.
export const dripRedeemData = (vault: DripVaultDescriptor, shares: number): Buffer =>
    vault.kind === "asset"
        ? createDripRedeemAssetPayload(shares)
        : createDripRedeemPayload(shares);

export const useDripRedeem = (vault: DripVaultDescriptor) => {
    const { connection } = useConnection();
    const { publicKey, sendTransaction } = useWallet();
    const wallet = useAnchorWallet();
    const {
        environment,
        priorityFeeLevel,
        dripProgramAddress,
    } = useContext(AppConfigContext);

    const dripProgram = new PublicKey(dripProgramAddress);
    const depositMint = new PublicKey(vault.depositMint);
    const signer = publicKey;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const redeem = useCallback(async (shares: number, feeRecipient: string) => {
        if (!signer || !wallet) {
            throw new Error("Please connect your wallet.");
        }

        if (shares <= 0) {
            throw new Error("Please enter an amount greater than 0.");
        }

        mixpanel.track('Drip Redeem Started', {
            'shares': shares,
            'vault': vault.id,
            'wallet': wallet.publicKey?.toBase58(),
        });

        setLoading(true);
        setError(null);

        const ixs: TransactionInstruction[] = [];

        try {
            const { vaultState, userPosition, reserve, shareMint } = deriveDripPdas(
                vault,
                dripProgram,
                signer
            );

            if (!userPosition) {
                throw new Error("Please connect your wallet.");
            }

            const userTokenAccount = await getAssociatedTokenAddress(
                depositMint,
                signer,
                true
            );

            ixs.push(createAssociatedTokenAccountIdempotentInstruction(
                signer,
                userTokenAccount,
                signer,
                depositMint
            ));

            const userShareAccount = await getAssociatedTokenAddress(
                shareMint,
                signer
            );

            // Anchor enforces the fee-recipient account's mint and authority even
            // when withdrawal_fee is 0, so it has to exist.
            const feeRecipientPubkey = new PublicKey(feeRecipient);
            const feeRecipientTokenAccount = await getAssociatedTokenAddress(
                depositMint,
                feeRecipientPubkey,
                true
            );

            ixs.push(createAssociatedTokenAccountIdempotentInstruction(
                signer,
                feeRecipientTokenAccount,
                feeRecipientPubkey,
                depositMint
            ));

            // redeem settles accrued yield before pricing the redemption, so the
            // vault's rate oracle is required: 2 accounts for kySOL, 1 for asset
            // vaults. Omitting them fails with AccountNotEnoughKeys (3005) — i.e.
            // withdrawals stop — so this must ship before the program upgrade.
            const oracleAccounts = await fetchDripOracleAccounts(
                dripProgramFor(dripIdl, dripProgramAddress, connection),
                vault,
                dripProgram
            );

            // The two families share this prefix — ky_sol_mint occupies the slot
            // deposit_mint does — and diverge only in the trailing rate-oracle
            // accounts, which is why those come from fetchDripOracleAccounts rather
            // than being spelled out here. The old note said to split this list if a
            // future IDL diverged; it now does, by exactly that tail.
            const keys = [
                { pubkey: vaultState, isSigner: false, isWritable: true },
                { pubkey: userPosition, isSigner: false, isWritable: true },
                { pubkey: reserve, isSigner: false, isWritable: true },
                { pubkey: userTokenAccount, isSigner: false, isWritable: true },
                { pubkey: userShareAccount, isSigner: false, isWritable: true },
                { pubkey: feeRecipientTokenAccount, isSigner: false, isWritable: true },
                { pubkey: shareMint, isSigner: false, isWritable: true },
                { pubkey: depositMint, isSigner: false, isWritable: false },
                { pubkey: signer, isSigner: true, isWritable: true },
                { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
                ...oracleAccounts,
            ];

            ixs.push(new TransactionInstruction({
                keys,
                programId: dripProgram,
                data: dripRedeemData(vault, shares),
            }));

            const success = await sendTx(environment, connection, signer, sendTransaction, ixs, priorityFeeLevel);

            if (success) {
                mixpanel.track('Drip Redeem Completed', {
                    'shares': shares,
                    'vault': vault.id,
                    'wallet': wallet.publicKey?.toBase58(),
                });
            }

            return success;
        } catch (err) {
            // Surface a clean message and re-throw so the caller can react
            // (e.g. distinguish a user cancellation from a real failure).
            setError(getWalletErrorMessage(err, "The withdrawal transaction failed. Please try again."));
            throw err;
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signer, wallet, vault.id, vault.kind, vault.depositMint]);

    return { redeem, loading, error };
};
